import { useMemo, useState, useEffect, useRef } from 'react';
import { Japanese } from "flatpickr/dist/l10n/ja.js";

/**
 * 画面サイズに応じた表示月数を提供するカスタムフック
 * @param {Object} containerRef - カレンダーを含むコンテナへの参照（オプション）
 * @returns {number} 表示する月の数
 */
export const useResponsiveMonths = (containerRef) => {
  const [showMonths, setShowMonths] = useState(1);
  const calculationTimerRef = useRef(null);
  const lastCalculatedWidthRef = useRef(0); // 最後に計算した幅
  const instanceIdRef = useRef(`calendar-${Math.random().toString(36).substring(2, 9)}`);

  useEffect(() => {
    // 特定のカレンダーインスタンスの幅を取得する関数
    const getCalendarContainerWidth = () => {
      // 1. 明示的に渡されたコンテナrefを最優先
      if (containerRef && containerRef.current) {
        const width = containerRef.current.clientWidth;
        return width;
      }

      // 2. instanceIdに基づいて特定のカレンダーを識別
      const calendar = document.querySelector(`.flatpickr-calendar[data-instance="${instanceIdRef.current}"]`);
      const calendarWrapper = document.querySelector(`.flatpickr-wrapper[data-instance="${instanceIdRef.current}"]`);
      
      if (calendar) {
        // インラインモードの場合は親要素の幅を取得
        if (calendar.classList.contains('inline')) {
          const parentWidth = calendar.parentElement?.clientWidth || 0;
          if (parentWidth > 100) {
            return parentWidth;
          }
        }
      }
      
      if (calendarWrapper) {
        const wrapperWidth = calendarWrapper.clientWidth;
        if (wrapperWidth > 100) {
          return wrapperWidth;
        }
      }
      
      // 3. この特定のインスタンスに関連する入力フィールドを検索
      const input = document.querySelector(`.flatpickr-input[data-instance="${instanceIdRef.current}"]`);
      if (input) {
        // 親要素のスタイルを確認
        const parentNode = input.parentElement;
        if (parentNode) {
          const parentWidth = parentNode.clientWidth;
          const parentStyle = window.getComputedStyle(parentNode);
          
          // widthが明示的に設定されている場合
          if (parentWidth > 100 && 
              ((parentStyle.width !== 'auto' && 
               !parentStyle.width.includes('%')) || 
               parentNode.getAttribute('width'))) {
            return parentWidth;
          }
          
          // u-row内の幅指定要素
          if (parentNode.classList.contains('u-row')) {
            // width属性またはスタイルを持つ子要素を探す
            const siblings = Array.from(parentNode.children);
            for (const sibling of siblings) {
              if (sibling.contains(input)) {
                const siblingWidth = sibling.clientWidth;
                const widthAttr = sibling.getAttribute('width');
                
                if (widthAttr && widthAttr.includes('%')) {
                  const percentage = parseInt(widthAttr) / 100;
                  const calculatedWidth = parentWidth * percentage;
                  return calculatedWidth;
                } else if (siblingWidth > 100) {
                  return siblingWidth;
                }
              }
            }
          }
        }
      }
      
      // 4. フォールバック - ウィンドウ幅の比率に基づく推定
      // 特定できない場合は、ウィンドウ幅に基づいて控えめな値を返す
      return Math.min(500, window.innerWidth * 0.8);
    };

    // 月数計算とカレンダー更新
    const calculateAndUpdateMonths = () => {
      // 幅を取得
      const containerWidth = getCalendarContainerWidth();
      
      // 前回と同じ幅の場合はスキップ（変化がない場合は処理しない）
      if (Math.abs(lastCalculatedWidthRef.current - containerWidth) < 10) {
        return;
      }
      
      // 現在の幅を記録
      lastCalculatedWidthRef.current = containerWidth;
      
      // 月数の決定ロジック
      const doubleMonthWidth = 592; // 2ヶ月のカレンダーの最小幅 
      const tripleMonthWidth = 888; // 3ヶ月のカレンダーの最小幅
      
      let newMonthCount = 1; // デフォルト値
      
      if (containerWidth >= tripleMonthWidth) {
        newMonthCount = 3;
      } else if (containerWidth >= doubleMonthWidth) {
        newMonthCount = 2;
      } else {
        newMonthCount = 1;
      }
      
      // カレンダー要素の識別と更新
      const updateCalendarUI = () => {
        // 特定のインスタンスIDに関連するカレンダー要素を探す
        const calendar = document.querySelector(`.flatpickr-calendar[data-instance="${instanceIdRef.current}"]`);
        if (!calendar) return;
        
        // クラスの更新
        calendar.className = calendar.className.replace(/showMonths-\d+/g, '');
        calendar.classList.add(`showMonths-${newMonthCount}`);
        
        if (newMonthCount > 1) {
          calendar.classList.add('multiMonth');
        } else {
          calendar.classList.remove('multiMonth');
        }
      };
      
      // UIの更新（レンダリング完了後に実行）
      setTimeout(updateCalendarUI, 0);
      
      // 状態を更新（変更がある場合のみ）
      if (showMonths !== newMonthCount) {
        setShowMonths(newMonthCount);
      }
    };
    
    // スロットリング関数 - 短時間に何度も実行されないようにする
    const throttledCalculation = () => {
      if (calculationTimerRef.current) {
        clearTimeout(calculationTimerRef.current);
      }
      
      calculationTimerRef.current = setTimeout(() => {
        calculateAndUpdateMonths();
        calculationTimerRef.current = null;
      }, 100); // 100ms間隔でスロットリング
    };
    
    // 初回レンダリング後に実行
    const initialTimerId = setTimeout(calculateAndUpdateMonths, 10);
    
    // リサイズイベントリスナー（スロットリングあり）
    window.addEventListener('resize', throttledCalculation);
    
    // FlatpickrのDOMが生成または更新された時のオブザーバー
    const setupInstanceId = () => {
      // data-instanceが付与されていない要素にIDを設定
      const calendars = document.querySelectorAll('.flatpickr-calendar:not([data-instance])');
      calendars.forEach(cal => {
        cal.setAttribute('data-instance', instanceIdRef.current);
      });
      
      const wrappers = document.querySelectorAll('.flatpickr-wrapper:not([data-instance])');
      wrappers.forEach(wrapper => {
        wrapper.setAttribute('data-instance', instanceIdRef.current);
      });
      
      const inputs = document.querySelectorAll('.flatpickr-input:not([data-instance])');
      inputs.forEach(input => {
        input.setAttribute('data-instance', instanceIdRef.current);
      });
      
      // IDが設定されたらもう一度計算
      if (calendars.length > 0 || wrappers.length > 0 || inputs.length > 0) {
        calculateAndUpdateMonths();
      }
    };
    
    // DOMの変更を監視する最適化されたオブザーバー
    const observer = new MutationObserver((mutations) => {
      let needsUpdate = false;
      
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // flatpickr関連の要素が追加されたか確認
          const addedNodes = Array.from(mutation.addedNodes);
          for (const node of addedNodes) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.classList?.contains('flatpickr-calendar') || 
                  node.classList?.contains('flatpickr-wrapper') ||
                  node.querySelector?.('.flatpickr-calendar, .flatpickr-wrapper')) {
                needsUpdate = true;
                break;
              }
            }
          }
        } else if (mutation.type === 'attributes' && 
                  (mutation.target.classList?.contains('flatpickr-calendar') ||
                   mutation.target.classList?.contains('flatpickr-wrapper'))) {
          needsUpdate = true;
        }
        
        if (needsUpdate) break;
      }
      
      if (needsUpdate) {
        // インスタンスIDを設定
        setupInstanceId();
        // 少し遅延させて計算（DOM更新完了を待つ）
        setTimeout(throttledCalculation, 20);
      }
    });
    
    // 初期設定
    setupInstanceId();
    
    // 監視開始
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'width']
    });
    
    // クリーンアップ
    return () => {
      clearTimeout(initialTimerId);
      if (calculationTimerRef.current) {
        clearTimeout(calculationTimerRef.current);
      }
      window.removeEventListener('resize', throttledCalculation);
      observer.disconnect();
    };
  }, [containerRef, showMonths]);

  return showMonths;
};

/**
 * カレンダーのロケール設定を提供するカスタムフック
 * @returns {Object} カスタムロケール設定
 */
export const useCalendarLocale = () => {
  return useMemo(() => ({
    ...Japanese,
    weekdays: {
      shorthand: ['日', '月', '火', '水', '木', '金', '土'],
      longhand: ['日曜日', '月曜日', '火曜日', '水曜日', '木曜日', '金曜日', '土曜日']
    },
    months: {
      shorthand: ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'],
      longhand: ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER']
    }
  }), []);
};

/**
 * 年の<input>を<select>に置換し、任意の年へ即座にジャンプできるようにする
 * @param {Object} instance - flatpickrインスタンス
 * @param {number} minYear - 選択可能な最小年
 * @param {number} maxYear - 選択可能な最大年
 */
export const replaceYearInputWithSelect = (instance, minYear, maxYear) => {
  if (!instance || !instance.calendarContainer) return;

  const yearInputs = instance.calendarContainer.querySelectorAll('.numInputWrapper');
  yearInputs.forEach((wrapper) => {
    const input = wrapper.querySelector('input.cur-year');
    if (!input || wrapper.querySelector('.cur-year-select')) return;

    const select = document.createElement('select');
    select.className = 'cur-year-select';
    select.setAttribute('aria-label', 'Year');

    for (let y = minYear; y <= maxYear; y++) {
      const opt = document.createElement('option');
      opt.value = y;
      opt.textContent = y;
      if (y === instance.currentYear) opt.selected = true;
      select.appendChild(opt);
    }

    select.addEventListener('change', (e) => {
      instance.changeYear(parseInt(e.target.value));
    });

    wrapper.replaceChild(select, input);
  });
};

/**
 * 年セレクトの選択値をflatpickrの現在年と同期する
 * @param {Object} instance - flatpickrインスタンス
 */
const syncYearSelect = (instance) => {
  if (!instance || !instance.calendarContainer) return;

  const selects = instance.calendarContainer.querySelectorAll('.cur-year-select');
  selects.forEach((select) => {
    if (parseInt(select.value) !== instance.currentYear) {
      select.value = instance.currentYear;
    }
  });
};

/**
 * カレンダーの設定オプションを生成するカスタムフック
 * @param {Object} params - 設定パラメータ
 * @param {string} params.mode - 選択モード ("single", "range", "multiple")
 * @param {boolean} params.inline - インライン表示の有無
 * @param {Date} params.minDate - 選択可能な最小日付
 * @param {Date} params.maxDate - 選択可能な最大日付
 * @param {boolean} params.allowInput - 入力を許可するかどうか
 * @param {string} params.dateFormat - 日付フォーマット
 * @param {number[]} params.yearRange - 年の範囲 [minYear, maxYear]
 * @param {Object} params.additionalOptions - 追加のflatpickrオプション
 * @returns {Object} flatpickrに渡すオプション
 */
export const useCalendarOptions = ({
  mode = "single",
  inline = false,
  minDate,
  maxDate,
  allowInput = false,
  dateFormat,
  yearRange,
  additionalOptions = {}
}) => {
  const customLocale = useCalendarLocale();
  const instanceIdRef = useRef(`calendar-${Math.random().toString(36).substring(2, 9)}`);

  return useMemo(() => {
    const currentYear = new Date().getFullYear();
    const [minYear, maxYear] = yearRange || [currentYear - 100, currentYear + 10];

    const options = {
      locale: customLocale,
      disableMobile: true,
      static: true,
      monthSelectorType: "dropdown",
      shorthandCurrentMonth: true,
      prevArrow: "<span class='material-symbols-rounded'>navigate_before</span>",
      nextArrow: "<span class='material-symbols-rounded'>navigate_next</span>",
      ...additionalOptions,
      onReady: (selectedDates, dateStr, instance) => {
        // インスタンスIDの設定
        const instanceId = instanceIdRef.current;
        
        if (instance && instance.calendarContainer) {
          instance.calendarContainer.setAttribute('data-instance', instanceId);
        }
        if (instance && instance.input) {
          instance.input.setAttribute('data-instance', instanceId);
        }
        if (instance && instance.altInput) {
          instance.altInput.setAttribute('data-instance', instanceId);
        }
        
        // カレンダーが初期化された後にDOMを操作
        setTimeout(() => {
          if (!instance || !instance.calendarContainer) return;
          
          const calendar = instance.calendarContainer;
          const monthsElement = calendar.querySelector('.flatpickr-months');
          
          if (monthsElement) {
            const showMonths = instance.config.showMonths || 1;
            calendar.classList.add(`showMonths-${showMonths}`);
            
            if (showMonths > 1) {
              calendar.classList.add('multiMonth');
            } else {
              calendar.classList.remove('multiMonth');
            }
          }

          replaceYearInputWithSelect(instance, minYear, maxYear);
        }, 0);
        
        if (additionalOptions.onReady) {
          additionalOptions.onReady(selectedDates, dateStr, instance);
        }
      },
      onMonthChange: (selectedDates, dateStr, instance) => {
        syncYearSelect(instance);
        if (additionalOptions.onMonthChange) {
          additionalOptions.onMonthChange(selectedDates, dateStr, instance);
        }
      },
      onYearChange: (selectedDates, dateStr, instance) => {
        syncYearSelect(instance);
        if (additionalOptions.onYearChange) {
          additionalOptions.onYearChange(selectedDates, dateStr, instance);
        }
      }
    };

    // dateFormat の優先順位:
    //  1. props で明示的に渡された `dateFormat`
    //  2. `additionalOptions.dateFormat`（呼び出し側が `options={{ dateFormat: ... }}` で指定）
    //  3. `enableTime: true` のときは `Y年n月j日（D） H:i`、それ以外は `Y年n月j日（D）`
    //
    // 旧実装は (1) || デフォルト で (2) を握り潰しており、さらに enableTime の有無を
    // 見ずに常に日付のみのデフォルトを使っていた。これにより新規オファーの納期/有効期限
    // など時刻ピッカー付きフィールドでも入力欄に時刻が出ず、時刻なしフォームと
    // 区別がつかなくなっていた。
    const additionalDateFormat = additionalOptions.dateFormat;
    const enableTime = !!additionalOptions.enableTime;

    if (inline) {
      options.inline = true;
      options.mode = mode === "range" ? "range" : mode === "multiple" ? "multiple" : "single";
      options.dateFormat = dateFormat || additionalDateFormat || "Y-M-d";
      options.position = "auto center";
    } else {
      options.appendTo = document.body;
      options.mode = mode === "range" ? "range" : mode === "multiple" ? "multiple" : "single";
      const defaultDateFormat = enableTime ? "Y年n月j日（D） H:i" : "Y年n月j日（D）";
      options.dateFormat = dateFormat || additionalDateFormat || defaultDateFormat;
    }

    if (maxDate) options.maxDate = maxDate;
    if (minDate) options.minDate = minDate;
    if (allowInput !== undefined) options.allowInput = allowInput;

    return options;
  }, [customLocale, mode, inline, minDate, maxDate, allowInput, dateFormat, yearRange, additionalOptions]);
};

/**
 * 日付を日本語フォーマットに変換
 * @param {Date} date - 変換する日付
 * @returns {string} フォーマットされた日付
 */
export const formatDate = (date) => {
  if (!date) return '';
  
  const d = new Date(date);
  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  const day = d.getDate();
  
  const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
  const weekday = weekdays[d.getDay()];
  
  return `${year}年${month}月${day}日（${weekday}）`;
}; 